import Image from 'next/image';
import Link from 'next/link';
import { Heading } from '../ui/heading/heading';
import { MoveDownRight } from 'lucide-react';
import type { TApiImage } from '@/shared/apiComponentTypes';
import { URLBuilder } from '@/utils/URLBuilder';
import { getImageSource } from '@/shared/image';

export type TBlogGalleryPost = {
  image: TApiImage['src'];
  title: string;
  slug: string;
  featured?: boolean;
  allowTwoLineTitle?: boolean;
};

export const BlogGalleryPost = ({ image, title, slug, featured = false, allowTwoLineTitle = false }: TBlogGalleryPost) => {
  const url = URLBuilder.paths.getBlogPost(slug);
  const coverImage = getImageSource(image, featured ? 'medium' : 'small');

  if (!coverImage) {
    return null;
  }

  if (featured) {
    return (
      <Link href={url} className="group lg:col-span-2">
        <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-neutral-800 bg-neutral-900/40 lg:h-[380px] lg:flex-row">
          <div className="relative w-full lg:w-1/2">
            <Image
              alt={coverImage.alt}
              src={coverImage.url}
              width={coverImage.width}
              height={coverImage.height}
              sizes="(max-width: 768px) 100vw, 50vw"
              quality={70}
              className="h-64 w-full object-cover transition-all lg:h-full"
            />
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-backgroundLight/80 to-transparent lg:rounded-none" />
          </div>

          <div className="flex w-full flex-col justify-center gap-4 p-6 lg:w-1/2 lg:p-10">
            <div className="flex items-center gap-3 text-xs font-oswald uppercase tracking-[0.35em] text-neutral-400">
              <span className="inline-block h-1 w-6 rounded-full bg-action" />
              nowy wpis
            </div>
            <Heading variant="h3" className="text-3xl text-white transition-colors group-hover:text-action lg:text-4xl">
              {title}
            </Heading>
            <div className="flex items-center gap-3 text-sm text-action">
              <span>czytaj</span>
              <MoveDownRight className="transition-transform group-hover:-rotate-90" size={24} />
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link href={url} className="group">
      <div className="relative h-full w-full overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900/40">
        <Image
          alt={coverImage.alt}
          src={coverImage.url}
          width={coverImage.width}
          height={coverImage.height}
          sizes="(max-width: 768px) 100vw, 50vw"
          quality={70}
          className="h-60 w-full object-cover transition-all"
        />

        <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-backgroundLight to-transparent" />

        <div className="absolute inset-0 flex flex-col justify-end gap-3 rounded-2xl p-5">
          <Heading
            variant="h3"
            className={`text-2xl text-white transition-colors group-hover:text-action text-balance ${allowTwoLineTitle ? 'line-clamp-1 lg:line-clamp-2' : 'line-clamp-1'}`}
          >
            {title}
          </Heading>
          <div className="flex items-center gap-2 text-sm text-action">
            <span>czytaj</span>
            <MoveDownRight className="transition-transform group-hover:-rotate-90" size={18} />
          </div>
        </div>
      </div>
    </Link>
  );
};
